import React, { useMemo } from 'react';
import { useTheme, type Theme } from '@mui/material/styles';
import type { EChartsOption, LineSeriesOption } from 'echarts';

import MultilineChart from 'components/Charts/MultilineChart';
import { getGoalTargetProjectionData } from './data';
import type { GoalTargetProjectionProps } from './types';

const applyGoalTargetProjectionStyle =
  (theme: Theme) =>
  (option: EChartsOption): EChartsOption => {
    const series = (Array.isArray(option.series) ? option.series : [option.series]).filter(
      Boolean,
    ) as LineSeriesOption[];
    return {
      ...option,
      tooltip: {
        ...option.tooltip,
        axisPointer: { show: false, type: 'none' },
      },
      series: series.map((seriesItem) => ({
        ...seriesItem,
        lineStyle: { ...seriesItem.lineStyle, color: theme.palette.success.main, opacity: 1, type: 'dashed' },
        itemStyle: {
          ...seriesItem.itemStyle,
          color: theme.palette.background.paper,
          borderColor: theme.palette.savingsGoal.spentBar,
          borderWidth: 2,
        },
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 8,
        emphasis: { ...seriesItem.emphasis, scale: 1.5 },
      })),
    };
  };

export const GoalTargetProjection = ({
  targetAmount,
  savedSoFar,
  contributionAmount,
  startDate,
  targetDate,
}: GoalTargetProjectionProps): React.JSX.Element => {
  const theme = useTheme<Theme>();
  const data = useMemo(
    () => getGoalTargetProjectionData({ targetAmount, savedSoFar, contributionAmount, startDate, targetDate }),
    [targetAmount, savedSoFar, contributionAmount, startDate, targetDate],
  );
  const optionOverrides = useMemo(() => applyGoalTargetProjectionStyle(theme), [theme]);

  return (
    <MultilineChart
      data={data}
      orderedFields={['target']}
      height={380}
      yAxisZeroStart
      areaOpacity={0}
      clipHoverLineToArea={false}
      colorOverrides={[theme.palette.success.main]}
      seriesNames={{ target: 'Target' }}
      optionOverrides={optionOverrides}
    />
  );
};

export default GoalTargetProjection;
